import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Building2, Users, Shield, CreditCard, Paintbrush, Fingerprint, Link2,
  Settings, ChevronLeft, ChevronRight, Sparkles, Copy, Loader2, Search,
  Globe, Database, Send, AlertTriangle, Layers, Check, Plus, Trash2,
  ArrowRight, Key, RefreshCw, Brain, MapPin, Layout,
} from 'lucide-react';
import TenantManagement from './TenantManagement';
import SidebarLayout from './SidebarLayout';
import BillingPage from './BillingPage';
import PropertyProfilesManager from './PropertyProfilesManager';
import { TenantHealthDashboard } from './AdminFeatures';
import PlatformMetrics from './PlatformMetrics';
import AnalyticsDashboard from './AnalyticsDashboard';
import { TenantBillingTab, TenantProposalTab } from './TenantBillingUI';
import { RevenueDashboard, CustomerHealth, EmailCampaigns, NpsFeedback, UsageMetering } from './CustomerOpsViews';
import CMSAdmin from './CMSAdmin';
import { DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function PortalUsersManager({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'vendor_admin', entity_id: '' });
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [brands, setBrands] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [uRes, vRes, bRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/portal-users`, { headers }),
        axios.get(`${BACKEND_URL}/api/vendors?limit=500`, { headers }),
        axios.get(`${BACKEND_URL}/api/brands`, { headers }),
      ]);
      setUsers(uRes.data.users || []);
      setVendors(vRes.data.vendors || []);
      setBrands(bRes.data || []);
    } catch (e) { toast.error('Failed to load portal users'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    let list = users;
    if (filterRole !== 'all') list = list.filter(u => u.role === filterRole);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.entity_name?.toLowerCase().includes(q));
    }
    return list;
  }, [users, search, filterRole]);

  const createUser = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/portal-users`, createForm, { headers });
      toast.success('Portal user created');
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-5xl" data-testid="portal-users-manager">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Portal Users</h2>
            <p className="text-sm text-muted-foreground">Manage vendor and brand portal access. These users can update product details and submit changes for approval.</p>
          </div>
          <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => { setCreateForm({ name: '', email: '', password: '', role: 'vendor_admin', entity_id: '' }); setCreateOpen(true); }}>
            <Plus className="h-3 w-3" /> New Portal User
          </Button>
        </div>
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs">
            <option value="all">All Roles</option>
            <option value="vendor_admin">Vendor Admins</option>
            <option value="brand_admin">Brand Admins</option>
          </select>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No portal users yet</p>
            <p className="text-xs mt-1">Create vendor or brand portal users to give external partners access</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-muted/50">
                <th className="p-2.5 text-left text-xs font-semibold">User</th>
                <th className="p-2.5 text-left text-xs font-semibold">Role</th>
                <th className="p-2.5 text-left text-xs font-semibold">Organization</th>
                <th className="p-2.5 text-center text-xs font-semibold">Status</th>
                <th className="p-2.5 text-center text-xs font-semibold">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className="border-t hover:bg-muted/20">
                    <td className="p-2.5"><div className="font-medium">{u.name}</div><div className="text-[10px] text-muted-foreground">{u.email}</div></td>
                    <td className="p-2.5"><Badge className={`text-[9px] ${u.role === 'vendor_admin' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{u.role === 'vendor_admin' ? 'Vendor' : 'Brand'}</Badge></td>
                    <td className="p-2.5 text-xs">{u.entity_name || '—'}</td>
                    <td className="p-2.5 text-center"><Badge className={`text-[9px] ${u.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{u.active !== false ? 'Active' : 'Disabled'}</Badge></td>
                    <td className="p-2.5 text-center">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                        if (!window.confirm(`Delete ${u.name}?`)) return;
                        try { await axios.delete(`${BACKEND_URL}/api/admin/portal-users/${u.id}`, { headers }); toast.success('Deleted'); fetchAll(); } catch (e) { toast.error('Failed'); }
                      }}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Portal User</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" />
              <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} placeholder="email@company.com" />
              <Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (6+ chars)" />
              <div>
                <label className="text-xs font-medium">Role</label>
                <select value={createForm.role} onChange={e => { setCreateForm(p => ({ ...p, role: e.target.value, entity_id: '' })); }} className="w-full h-9 rounded-md border bg-background px-3 text-sm mt-1">
                  <option value="vendor_admin">Vendor Admin</option>
                  <option value="brand_admin">Brand Admin</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">{createForm.role === 'vendor_admin' ? 'Vendor' : 'Brand'}</label>
                <select value={createForm.entity_id} onChange={e => setCreateForm(p => ({ ...p, entity_id: e.target.value }))} className="w-full h-9 rounded-md border bg-background px-3 text-sm mt-1">
                  <option value="">Select...</option>
                  {createForm.role === 'vendor_admin'
                    ? vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)
                    : brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)
                  }
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={createUser} disabled={saving || !createForm.name || !createForm.email || createForm.password.length < 6 || !createForm.entity_id}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ScrollArea>
  );
}

const DEFAULT_PLANS = [
  { id: 'core', name: 'Core', desc: 'Visibility & essentials', rate: 1.00, features: ['Stack map (7 layouts)', 'Platform & vendor database', 'Essential reports', 'Property profiles'] },
  { id: 'plus', name: 'Plus', desc: 'Governance & analytics', rate: 2.00, features: ['Everything in Core', 'All 40+ reports + CSV', 'Brand compliance', 'Contract management'] },
  { id: 'pro', name: 'Professional', desc: 'AI, ops & financial intel', rate: 3.50, features: ['Everything in Plus', '15 AI assistants + Ask Your Stack', 'Vendor Watch quarterly briefs', 'Financial Intelligence Hub'] },
  { id: 'enterprise', name: 'Enterprise', desc: 'SSO, white-label, API', rate: 5.00, features: ['Everything in Professional', 'Enterprise SSO & 2FA', 'White-label branding', '15 ERP connectors', 'Webhooks & API'] },
];

function GlobalPricingEditor({ token, headers }) {
  const [plans, setPlans] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/global-pricing`, { headers })
      .then(r => {
        const saved = r.data;
        if (saved && saved.plans) {
          setPlans(saved.plans);
        } else {
          setPlans(DEFAULT_PLANS);
        }
      })
      .catch(() => setPlans(DEFAULT_PLANS));
  }, [headers]);

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/global-pricing`, { plans }, { headers });
      toast.success('Published pricing saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updatePlan = (idx, field, value) => {
    setPlans(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const updateFeature = (planIdx, featIdx, value) => {
    setPlans(prev => prev.map((p, i) => {
      if (i !== planIdx) return p;
      const f = [...(p.features || [])];
      f[featIdx] = value;
      return { ...p, features: f };
    }));
  };

  const addFeature = (planIdx) => {
    setPlans(prev => prev.map((p, i) => i === planIdx ? { ...p, features: [...(p.features || []), ''] } : p));
  };

  const removeFeature = (planIdx, featIdx) => {
    setPlans(prev => prev.map((p, i) => i === planIdx ? { ...p, features: (p.features || []).filter((_, fi) => fi !== featIdx) } : p));
  };

  if (!plans) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-4xl space-y-6">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Published Plans & Pricing</h2>
          <p className="text-xs text-muted-foreground mt-1">Edit the public-facing plan tiers, rates, and included features. Changes apply to the marketing site and default proposals.</p>
        </div>

        {plans.map((plan, pi) => (
          <div key={plan.id} className="border rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Plan Name</Label>
                <Input value={plan.name} onChange={e => updatePlan(pi, 'name', e.target.value)} className="h-8 text-sm font-bold" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={plan.desc} onChange={e => updatePlan(pi, 'desc', e.target.value)} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">PRPM Rate ($/room/mo)</Label>
                <Input type="number" step="0.25" value={plan.rate} onChange={e => updatePlan(pi, 'rate', parseFloat(e.target.value) || 0)} className="h-8 text-sm font-bold" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Included Features</Label>
              <div className="space-y-1 mt-1">
                {(plan.features || []).map((feat, fi) => (
                  <div key={fi} className="flex gap-1.5">
                    <Input value={feat} onChange={e => updateFeature(pi, fi, e.target.value)} className="h-7 text-xs flex-1" />
                    <button onClick={() => removeFeature(pi, fi)} className="text-xs text-destructive px-1 hover:bg-red-50 rounded">&times;</button>
                  </div>
                ))}
                <button onClick={() => addFeature(pi)} className="text-xs text-primary hover:underline">+ Add feature</button>
              </div>
            </div>
          </div>
        ))}

        <Button onClick={savePricing} disabled={saving} className="w-full gap-1.5" data-testid="save-global-pricing">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save Published Pricing
        </Button>
      </div>
    </ScrollArea>
  );
}

const SA_SECTIONS = [
  { label: 'Tenants', items: [
    { id: 'tenants', label: 'Manage Tenants', icon: Building2 },
    { id: 'manage-properties', label: 'Manage Properties', icon: MapPin },
    { id: 'clone', label: 'Clone & Copy', icon: Copy },
  ]},
  { label: 'Sales', items: [
    { id: 'tenant-billing', label: 'Tenant Billing', icon: DollarSign, roles: ['platform_owner', 'sales'] },
    { id: 'tenant-proposals', label: 'Proposals & Contracts', icon: Sparkles, roles: ['platform_owner', 'sales'] },
    { id: 'global-pricing', label: 'Published Plans', icon: CreditCard, roles: ['platform_owner', 'sales'] },
  ]},
  { label: 'Customer Ops', items: [
    { id: 'revenue', label: 'Revenue Dashboard', icon: DollarSign, roles: ['platform_owner', 'sales'] },
    { id: 'customer-health', label: 'Customer Health', icon: RefreshCw },
    { id: 'email-campaigns', label: 'Email Campaigns', icon: Send, roles: ['platform_owner', 'sales'] },
    { id: 'nps', label: 'NPS & Feedback', icon: Sparkles },
    { id: 'usage-metering', label: 'Usage Metering', icon: Layers, roles: ['platform_owner', 'technical_admin'] },
  ]},
  { label: 'Users', items: [
    { id: 'super-admins', label: 'Super Admins', icon: Shield },
    { id: 'all-users', label: 'All Users', icon: Users },
    { id: 'portal-users', label: 'Portal Users', icon: Globe },
    { id: 'cross-tenant', label: 'Cross-Tenant Access', icon: ArrowRight },
    { id: 'role-mgmt', label: 'Role Management', icon: Key, roles: ['platform_owner'] },
  ]},
  { label: 'Platform', items: [
    { id: 'tenant-health', label: 'Tenant Health', icon: ArrowRight },
    { id: 'platform-metrics', label: 'Platform Metrics', icon: Database, roles: ['platform_owner', 'technical_admin'] },
    { id: 'analytics', label: 'Analytics', icon: ArrowRight, roles: ['platform_owner', 'technical_admin'] },
    { id: 'global-db', label: 'Global Tech Database', icon: Database, roles: ['platform_owner', 'technical_admin'] },
    { id: 'push-data', label: 'Push Data to Tenant', icon: Send, roles: ['platform_owner', 'technical_admin'] },
    { id: 'global-settings', label: 'Global Settings', icon: Globe, roles: ['platform_owner', 'technical_admin'] },
  ]},
  { label: 'Marketing Site', items: [
    { id: 'marketing-cms', label: 'Marketing CMS', icon: Layout, roles: ['platform_owner'] },
  ]},
  { label: 'AI Tools', items: [
    { id: 'sa-wizard', label: 'Portfolio Wizard', icon: Brain },
  ]},
];

export default function SuperAdminConsole({ token }) {
  const [section, setSection] = useState(() => {
    // Deep-link: read ?section=... query param on mount so external links
    // (like the PublishBar 'Full editor' button) can jump straight to a section.
    const params = new URLSearchParams(window.location.search);
    return params.get('section') || 'tenants';
  });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Super admin role-based access
  const [superRole, setSuperRole] = useState('platform_owner');
  const [selectedTenantForBilling, setSelectedTenantForBilling] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/my-super-role`, { headers })
      .then(r => setSuperRole(r.data.role || 'platform_owner'))
      .catch(() => {});
  }, [headers]);

  // Filter sections by role
  const filteredSections = SA_SECTIONS.map(g => ({
    ...g,
    items: g.items.filter(i => !i.roles || i.roles.includes(superRole)),
  })).filter(g => g.items.length > 0);

  // Tenants data
  const [tenants, setTenants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Feature flags
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [savingFlags, setSavingFlags] = useState(false);

  // Clone dialog
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);

  // AI Wizard
  const [wizardMessages, setWizardMessages] = useState([{ role: 'ai', text: 'Welcome to the Super Admin Portfolio Wizard. I can help you:\n\n- Create new tenants with properties\n- Copy settings between tenants\n- Batch-configure feature flags\n- Set up billing plans\n- Push tech database products to tenants\n\nWhat would you like to do?' }]);
  const [wizardInput, setWizardInput] = useState('');
  const [wizardLoading, setWizardLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, uRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tenants`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers }),
      ]);
      setTenants(tRes.data.tenants || tRes.data || []);
      setAllUsers(uRes.data.users || uRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Feature flags
  const loadFeatureFlags = async (tenantId) => {
    setSelectedTenant(tenantId);
    try {
      const t = tenants.find(t => t.id === tenantId);
      setFeatureFlags(t?.features || {});
    } catch { setFeatureFlags({}); }
  };

  const saveFeatureFlags = async () => {
    if (!selectedTenant) return;
    setSavingFlags(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${selectedTenant}/features`, featureFlags, { headers });
      toast.success('Feature flags saved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSavingFlags(false); }
  };

  // Clone tenant
  const cloneTenant = async () => {
    if (!cloneSource || !cloneName) return;
    setCloning(true);
    try {
      // Create new tenant
      const res = await axios.post(`${BACKEND_URL}/api/admin/tenants`, { name: cloneName }, { headers });
      const newTid = res.data.id;
      // Copy feature flags from source
      const source = tenants.find(t => t.id === cloneSource);
      if (source?.features) {
        await axios.put(`${BACKEND_URL}/api/admin/tenants/${newTid}/features`, source.features, { headers });
      }
      toast.success(`Tenant "${cloneName}" created (cloned from ${source?.name || cloneSource})`);
      setCloneOpen(false);
      setCloneName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clone failed');
    } finally { setCloning(false); }
  };

  // AI Wizard
  const sendWizardMessage = async () => {
    if (!wizardInput.trim() || wizardLoading) return;
    const userMsg = wizardInput.trim();
    setWizardMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setWizardInput('');
    setWizardLoading(true);
    try {
      const tenantList = tenants.map(t => `${t.name} (${t.id}, ${t.slug || ''})`).join(', ');
      const res = await axios.post(`${BACKEND_URL}/api/ai/onboarding-assistant`, {
        message: `[SUPER ADMIN CONTEXT] Tenants: ${tenantList}. Total users: ${allUsers.length}. The admin wants to: ${userMsg}`,
        context: { session: 'super-admin-wizard', mode: 'super-admin' },
      }, { headers });
      setWizardMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setWizardMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I had trouble processing that. Could you try again?' }]);
    } finally { setWizardLoading(false); }
  };

  const ALL_FEATURES = [
    { key: 'stack_map', label: 'Stack Map' },
    { key: 'tech_database', label: 'Tech Database' },
    { key: 'basic_reports', label: 'Basic Reports' },
    { key: 'advanced_reports', label: 'Advanced Reports' },
    { key: 'executive_reports', label: 'Executive Reports' },
    { key: 'risk_reports', label: 'Risk Reports' },
    { key: 'property_dashboard', label: 'Property Dashboard' },
    { key: 'governance', label: 'Governance' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'contracts', label: 'Contracts' },
    { key: 'corporate_stack', label: 'Corporate Stack' },
    { key: 'rfp_management', label: 'RFP Management' },
    { key: 'benchmarking', label: 'Benchmarking' },
    { key: 'vendor_portal', label: 'Vendor Portal' },
    { key: 'brand_portal', label: 'Brand Portal' },
    { key: 'export_pdf', label: 'PDF Export' },
    { key: 'export_csv', label: 'CSV Export' },
    { key: 'export_email', label: 'Email Reports' },
    { key: 'financial_hub', label: 'Financial Hub' },
    { key: 'ai_features', label: 'AI Features' },
    { key: 'tools', label: 'Tools Hub' },
    { key: 'white_label', label: 'White Label' },
  ];

  return (
    <SidebarLayout title="Global Admin" icon={Shield} groups={filteredSections} activeId={section} onSelect={setSection} accentColor="#7c3aed"
      footer={<div className="flex items-center gap-1.5 text-[9px] text-red-600 font-medium"><AlertTriangle className="h-3 w-3 shrink-0" /><span>Changes affect ALL tenants</span></div>}>
        {section === 'tenants' ? (
          <TenantManagement token={token} />
        ) : section === 'manage-properties' ? (
          <PropertyProfilesManager token={token} />
        ) : section === 'tenant-billing' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-4xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Tenant Billing & Pricing</h2>
              <p className="text-xs text-muted-foreground mb-4">Select a tenant to configure pricing, contracts, and billing.</p>
              <select value={selectedTenantForBilling} onChange={e => setSelectedTenantForBilling(e.target.value)}
                className="w-full h-10 rounded-lg border bg-background px-3 text-sm mb-4">
                <option value="">Select tenant...</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {selectedTenantForBilling && <TenantBillingTab token={token} tenantId={selectedTenantForBilling} />}
            </div>
          </ScrollArea>
        ) : section === 'tenant-proposals' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-4xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Proposals, Contracts & Negotiations</h2>
              <p className="text-xs text-muted-foreground mb-4">Generate AI proposals/contracts and track negotiations per tenant.</p>
              <select value={selectedTenantForBilling} onChange={e => setSelectedTenantForBilling(e.target.value)}
                className="w-full h-10 rounded-lg border bg-background px-3 text-sm mb-4">
                <option value="">Select tenant...</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {selectedTenantForBilling && <TenantProposalTab token={token} tenantId={selectedTenantForBilling} tenantName={tenants.find(t => t.id === selectedTenantForBilling)?.name || ''} />}
            </div>
          </ScrollArea>
        ) : section === 'global-pricing' ? (
          <GlobalPricingEditor token={token} headers={headers} />
        ) : section === 'revenue' ? (
          <RevenueDashboard token={token} />
        ) : section === 'customer-health' ? (
          <CustomerHealth token={token} />
        ) : section === 'email-campaigns' ? (
          <EmailCampaigns token={token} />
        ) : section === 'nps' ? (
          <NpsFeedback token={token} />
        ) : section === 'usage-metering' ? (
          <UsageMetering token={token} />
        ) : section === 'role-mgmt' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Super Admin Roles</h2>
              <p className="text-xs text-muted-foreground mb-4">Assign roles to super admin users to control what they can access.</p>
              <div className="space-y-3 mb-6">
                {[{role:'platform_owner',label:'Platform Owner',desc:'Full access to everything',color:'#7c3aed'},
                  {role:'sales',label:'Sales',desc:'Tenants, billing, proposals, contracts, negotiations. No tech admin.',color:'#059669'},
                  {role:'technical_admin',label:'Technical Admin',desc:'Metrics, analytics, global DB, support. No billing/pricing.',color:'#2563eb'}
                ].map(r => (
                  <div key={r.role} className="p-4 border rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
                      <span className="text-sm font-bold">{r.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{r.desc}</p>
                  </div>
                ))}
              </div>
              <h3 className="text-sm font-semibold mb-2">Super Admin Users</h3>
              <div className="border rounded-xl divide-y">
                {allUsers.filter(u => !u.tenant_id).map(u => (
                  <div key={u.id} className="flex items-center gap-3 p-3 text-sm">
                    <Shield className="h-4 w-4 text-purple-500 shrink-0" />
                    <div className="flex-1"><span className="font-medium">{u.name || u.email}</span></div>
                    <select value={u.super_role || 'platform_owner'} onChange={async (e) => {
                      try {
                        await axios.put(`${BACKEND_URL}/api/admin/users/${u.id}/super-role`, { super_role: e.target.value }, { headers });
                        toast.success(`Role updated for ${u.email}`);
                        fetchData();
                      } catch { toast.error('Failed'); }
                    }} className="rounded-md border bg-background px-2 py-1 text-xs">
                      <option value="platform_owner">Platform Owner</option>
                      <option value="sales">Sales</option>
                      <option value="technical_admin">Technical Admin</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        ) : section === 'clone' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-2xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Clone & Copy</h2>
              <p className="text-xs text-muted-foreground mb-6">Clone tenants with their feature flags and settings. Properties and users are not copied.</p>
              <div className="border rounded-xl p-4 space-y-4">
                <div>
                  <Label className="text-xs">Source Tenant</Label>
                  <select value={cloneSource} onChange={e => setCloneSource(e.target.value)}
                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm" data-testid="sa-clone-source">
                    <option value="">Select source...</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">New Tenant Name</Label>
                  <Input value={cloneName} onChange={e => setCloneName(e.target.value)} placeholder="e.g., Acme Hospitality Group" data-testid="sa-clone-name" />
                </div>
                <Button onClick={cloneTenant} disabled={cloning || !cloneSource || !cloneName} className="gap-1.5" data-testid="sa-clone-btn">
                  {cloning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                  Clone Tenant
                </Button>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'super-admins' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Super Admins</h2>
              <p className="text-xs text-muted-foreground mb-4">Users with platform-level access (no tenant_id). These users can manage all tenants.</p>
              <div className="border rounded-xl overflow-hidden">
                <div className="divide-y">
                  {allUsers.filter(u => !u.tenant_id).map(u => (
                    <div key={u.id} className="flex items-center gap-3 p-3 text-sm">
                      <Shield className="h-4 w-4 text-purple-500 shrink-0" />
                      <div className="flex-1">
                        <span className="font-medium">{u.name || u.email}</span>
                        <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                      </div>
                      <Badge className="text-[8px] bg-purple-100 text-purple-700">{u.role}</Badge>
                    </div>
                  ))}
                  {allUsers.filter(u => !u.tenant_id).length === 0 && (
                    <p className="text-sm text-muted-foreground p-6 text-center">No super admin users found.</p>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'all-users' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-4xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>All Users ({allUsers.length})</h2>
              <p className="text-xs text-muted-foreground mb-4">Every user across all tenants. Use the tenant switcher to manage users within a specific tenant.</p>
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2.5 font-semibold">Name</th>
                      <th className="p-2.5 font-semibold">Email</th>
                      <th className="p-2.5 font-semibold">Role</th>
                      <th className="p-2.5 font-semibold">Tenant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allUsers.map(u => {
                      const t = tenants.find(t => t.id === u.tenant_id);
                      return (
                        <tr key={u.id} className="hover:bg-muted/20">
                          <td className="p-2.5 font-medium">{u.name || '-'}</td>
                          <td className="p-2.5 font-mono">{u.email}</td>
                          <td className="p-2.5"><Badge variant="outline" className="text-[8px]">{u.role}</Badge></td>
                          <td className="p-2.5">{t ? t.name : u.tenant_id ? <span className="text-muted-foreground">{u.tenant_id}</span> : <Badge className="text-[8px] bg-purple-100 text-purple-700">Super Admin</Badge>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'portal-users' ? (
          <PortalUsersManager token={token} />
        ) : section === 'cross-tenant' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-4xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Cross-Tenant Access</h2>
              <p className="text-xs text-muted-foreground mb-4">Grant non-super-admin users visibility across multiple tenants. For portfolio overseers, regional directors, service providers, and brand representatives.</p>
              <div className="space-y-4">
                {/* Existing grants */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="p-3 bg-muted/30 border-b flex items-center justify-between">
                    <span className="text-xs font-semibold">Active Grants</span>
                    <Button size="sm" className="h-7 text-xs gap-1" onClick={() => {
                      const userId = prompt('User ID to grant access:');
                      const tenantIdStr = prompt('Tenant IDs (comma-separated):');
                      const level = prompt('Access level (read, reports, full):') || 'read';
                      const label = prompt('Role label (e.g., Portfolio Overseer):') || '';
                      if (userId && tenantIdStr) {
                        axios.post(`${BACKEND_URL}/api/admin/cross-tenant-access`, {
                          user_id: userId, tenant_ids: tenantIdStr.split(',').map(s => s.trim()), access_level: level, label,
                        }, { headers }).then(() => { toast.success('Access granted'); fetchData(); }).catch(err => toast.error(err.response?.data?.detail || 'Failed'));
                      }
                    }} data-testid="grant-cross-tenant-btn">
                      <Plus className="h-3 w-3" /> Grant Access
                    </Button>
                  </div>
                  <div className="divide-y">
                    {allUsers.filter(u => {
                      // Show users that have cross-tenant grants
                      return true; // We'll filter via grants
                    }).length === 0 ? (
                      <p className="text-sm text-muted-foreground p-6 text-center">No cross-tenant grants configured. Use &ldquo;Grant Access&rdquo; to allow a user to view multiple tenants.</p>
                    ) : null}
                  </div>
                </div>
                <div className="p-4 border rounded-xl bg-muted/20">
                  <h3 className="text-sm font-semibold mb-2">Access Levels</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex gap-3"><Badge className="text-[9px] bg-blue-100 text-blue-700 shrink-0 w-16 justify-center">read</Badge><span>View-only access to stack maps, reports, and dashboards across granted tenants.</span></div>
                    <div className="flex gap-3"><Badge className="text-[9px] bg-amber-100 text-amber-700 shrink-0 w-16 justify-center">reports</Badge><span>View reports and export data. No editing of assignments or settings.</span></div>
                    <div className="flex gap-3"><Badge className="text-[9px] bg-emerald-100 text-emerald-700 shrink-0 w-16 justify-center">full</Badge><span>Full admin access within each granted tenant (same as tenant admin).</span></div>
                  </div>
                </div>
                <div className="p-4 border rounded-xl bg-muted/20">
                  <h3 className="text-sm font-semibold mb-2">Use Cases</h3>
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    <p><strong>Portfolio Overseer:</strong> Ownership group that has multiple management companies (tenants)</p>
                    <p><strong>Regional Director:</strong> Manages properties across multiple tenants in a geographic area</p>
                    <p><strong>Service Provider:</strong> IT MSP or consultant managing tech for multiple hotel companies</p>
                    <p><strong>Brand Representative:</strong> Brand office monitoring compliance across management company tenants</p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'tenant-health' ? (
          <TenantHealthDashboard token={token} />
        ) : section === 'platform-metrics' ? (
          <PlatformMetrics token={token} />
        ) : section === 'analytics' ? (
          <AnalyticsDashboard token={token} />
        ) : section === 'global-db' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Global Tech Database</h2>
              <p className="text-xs text-muted-foreground mb-4">The global master catalog of platforms, products, and vendors. Changes here are shared across all tenants.</p>
              <div className="border rounded-xl p-6 text-center">
                <Database className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm font-medium mb-2">Use the Tech Database tab to manage the global catalog</p>
                <p className="text-xs text-muted-foreground">All platform types, products, and vendors are globally shared. The Tech Database tab provides full CRUD operations.</p>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'push-data' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Push Data to Tenant</h2>
              <p className="text-xs text-muted-foreground mb-4">Push global products, platforms, or templates to a specific tenant. Use the Corporate Stack feature within a tenant to push solution assignments.</p>
              <div className="border rounded-xl p-6 text-center">
                <Send className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm font-medium mb-2">Switch to a tenant, then use Corporate Stack</p>
                <p className="text-xs text-muted-foreground">Use the tenant switcher in the header to enter a tenant context, then navigate to Admin → Corporate Stack to push solutions.</p>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'global-settings' ? (
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Global Settings</h2>
              <p className="text-xs text-muted-foreground mb-4">Platform-wide defaults applied to new tenants.</p>
              <div className="space-y-3">
                <div className="border rounded-xl p-4">
                  <h3 className="text-sm font-semibold mb-2">Default Feature Set for New Tenants</h3>
                  <p className="text-xs text-muted-foreground">When creating a new tenant, these features are enabled by default. Modify per-tenant in Feature Flags.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['stack_map', 'tech_database', 'basic_reports', 'property_dashboard', 'export_pdf'].map(f => (
                      <Badge key={f} className="text-[9px] bg-emerald-100 text-emerald-700">{f.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </div>
                <div className="border rounded-xl p-4">
                  <h3 className="text-sm font-semibold mb-2">Platform Statistics</h3>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div className="text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{tenants.length}</div><div className="text-[10px] text-muted-foreground">Tenants</div></div>
                    <div className="text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{allUsers.length}</div><div className="text-[10px] text-muted-foreground">Users</div></div>
                    <div className="text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{allUsers.filter(u => !u.tenant_id).length}</div><div className="text-[10px] text-muted-foreground">Super Admins</div></div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : section === 'marketing-cms' ? (
          <CMSAdmin token={token} />
        ) : section === 'sa-wizard' ? (
          <div className="flex flex-col h-full">
            <div className="px-6 pt-4 pb-2">
              <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Brain className="h-5 w-5 text-purple-500" /> Super Admin Portfolio Wizard
              </h2>
              <p className="text-xs text-muted-foreground">AI assistant for tenant creation, portfolio setup, batch configuration, and data migration.</p>
            </div>
            <ScrollArea className="flex-1 px-6">
              <div className="space-y-3 pb-4">
                {wizardMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-purple-600 text-white' : 'bg-muted'}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {wizardLoading && <div className="flex justify-start"><div className="p-3 rounded-xl bg-muted"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
              </div>
            </ScrollArea>
            <div className="px-6 pb-4 pt-2 border-t flex gap-2">
              <Input value={wizardInput} onChange={e => setWizardInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendWizardMessage()}
                placeholder="e.g., Create a new tenant for Marriott with 20 properties..."
                className="flex-1" data-testid="sa-wizard-input" />
              <Button onClick={sendWizardMessage} disabled={wizardLoading || !wizardInput.trim()} className="gap-1.5 bg-purple-600 hover:bg-purple-700" data-testid="sa-wizard-send">
                <Sparkles className="h-3.5 w-3.5" /> Send
              </Button>
            </div>
          </div>
        ) : null}
    </SidebarLayout>
  );
}
